import React, { useMemo, useCallback } from 'react';
import {
  ListSubheader,
  IconButton,
  Typography,
  Checkbox,
  Tooltip
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { DateTime } from 'luxon';
import { FormattedMessage, useIntl } from 'react-intl';
import { useMeContext } from 'modules/me';
import { useFeatureFlag } from 'modules/common/hooks';
import { useAutoPilotContext } from 'modules/App/menu/items/Settings/AutoPilotContextProvider';
import { SmartDiaryEntry } from '../type';
import { useSmartDiaryCheckboxIcons } from '../hooks/useSmartDiaryCheckboxIcons';
import { useGroupCheckState } from './hooks';

type SmartDiaryListItemHeaderType = {
  title: string;
  onExpandCollapse: (title: string) => void;
  expanded?: boolean;
  smartEntries: SmartDiaryEntry[];
  setDuration?: React.Dispatch<React.SetStateAction<string>>;
};

type ListHeaderStyleProps = {
  expanded: boolean;
};

type UseStyleProps = {
  isAutoPilotEnabled: boolean;
  expanded: boolean;
};

const useStyles = makeStyles((theme) => ({
  container: {
    display: 'flex',
    justifyContent: 'space-between',
    backgroundColor: theme.palette.grey[200],
    alignItems: 'center',
    paddingLeft: theme.spacing(2),
    marginBottom: ({ expanded }: ListHeaderStyleProps) =>
      expanded ? theme.spacing(0) : theme.spacing(0.25),
    cursor: 'pointer',
    marginRight: theme.spacing(-1),
    zIndex: 2
  },
  icon: {
    margin: theme.spacing(0.25),
    padding: theme.spacing(0)
  },
  iconColor: {
    color: theme.palette.text.primary
  },
  title: {
    fontWeight: theme.typography.fontWeightMedium,
    color: theme.palette.text.secondary,
    paddingLeft: ({ isAutoPilotEnabled }: UseStyleProps) =>
      isAutoPilotEnabled ? theme.spacing(1.25) : theme.spacing(0.5)
  },
  daySelectionWrapper: {
    display: 'flex',
    alignItems: 'center'
  },
  actionContainer: {
    display: 'flex'
  },
  duration: {
    alignSelf: 'center',
    color: theme.palette.text.secondary,
    padding: theme.spacing(1, 0.75, 1, 0)
  }
}));
const useCheckBoxStyles = makeStyles((theme) => ({
  root: {
    color: theme.palette.grey[400],
    padding: theme.spacing(0.875, 1),
    '&:hover': {
      color: theme.palette.grey[500]
    },
    '&.Mui-checked': {
      color: theme.palette.grey[700]
    },
    '&.MuiCheckbox-indeterminate': {
      color: theme.palette.grey[700]
    },
    '&.Mui-checked:hover': {
      color: theme.palette.grey[700]
    }
  }
}));
export const SmartDiaryListItemHeader: React.FC<SmartDiaryListItemHeaderType> = ({
  title,
  onExpandCollapse,
  expanded = false,
  smartEntries
}: SmartDiaryListItemHeaderType) => {
  const { isAutoPilotEnabled } = useAutoPilotContext();
  const classes = useStyles({ expanded, isAutoPilotEnabled });
  const me = useMeContext();
  const luxonFormat =
    me?.locale?.dateTimeSettings?.shortDateFormat?.luxonPattern ||
    'MMM d, yyyy';

  const {
    checked,
    indeterminate,
    disable,
    onChange,
    totalDuration,
    fulltotalDuration
  } = useGroupCheckState(smartEntries);

  const onClick = useCallback(() => {
    onExpandCollapse(title);
  }, [title, onExpandCollapse]);
  const checkBoxStyles = useCheckBoxStyles();

  const isTiwScreenReaderEnabled = useFeatureFlag('isTiwScreenReaderEnabled');
  const isTiAutoAcceptUserSettingsEnabled = useFeatureFlag(
    'isTiAutoAcceptUserSettingsEnabled'
  );
  const { formatMessage } = useIntl();
  const headerDay = DateTime.fromISO(title).toFormat(luxonFormat);
  const { collapseIcon, expandIcon } = useMemo(
    () => ({
      collapseIcon: (
        <Tooltip title={<FormattedMessage id="collapse" />}>
          <IconButton
            className={classes.icon}
            data-testid={`expand-less-icon-${title}`}
            size="large"
            aria-label={
              isTiAutoAcceptUserSettingsEnabled && isTiwScreenReaderEnabled
                ? formatMessage(
                    { id: 'smartDiary.headerMsgCollapse' },
                    { date: headerDay, duration: fulltotalDuration }
                  )
                : ''
            }
            aria-labelledby={'collapse'}
          >
            <ExpandLessIcon className={classes.iconColor} />
          </IconButton>
        </Tooltip>
      ),
      expandIcon: (
        <Tooltip title={<FormattedMessage id="expand" />}>
          <IconButton
            className={classes.icon}
            data-testid={`expand-more-icon-${title}`}
            size="large"
            aria-labelledby={'expand'}
            aria-label={
              isTiAutoAcceptUserSettingsEnabled && isTiwScreenReaderEnabled
                ? formatMessage(
                    { id: 'smartDiary.headerMsgExpand' },
                    { date: headerDay, duration: fulltotalDuration }
                  )
                : ''
            }
          >
            <ExpandMoreIcon className={classes.iconColor} />
          </IconButton>
        </Tooltip>
      )
    }),
    [
      classes.icon,
      classes.iconColor,
      formatMessage,
      fulltotalDuration,
      headerDay,
      isTiAutoAcceptUserSettingsEnabled,
      isTiwScreenReaderEnabled,
      title
    ]
  );

  const selectMsg = formatMessage(
    {
      id: 'noteSelector.selectAllNotedDay'
    },
    { day: headerDay, duration: fulltotalDuration }
  );

  const inputProps = useMemo(
    () => ({
      inputProps: {
        'data-testid': `note-list-item-header-clickable_checkbox_${title}`,
        'aria-label': isTiwScreenReaderEnabled ? selectMsg : ''
      } as any
    }),
    [isTiwScreenReaderEnabled, selectMsg, title]
  );
  const iconProps = useSmartDiaryCheckboxIcons();
  const stopPropagation = useCallback((event) => event.stopPropagation(), []);

  return (
    <ListSubheader
      data-testid={`notes-list-item-header-${title}`}
      className={classes.container}
      onClick={onClick}
    >
      <div className={classes.daySelectionWrapper}>
        {!isAutoPilotEnabled && (
          <Checkbox
            color="default"
            classes={checkBoxStyles}
            checked={checked}
            indeterminate={indeterminate}
            disabled={disable}
            onChange={onChange}
            data-testid={`note-list-item-header-checkbox-${title}`}
            onClick={stopPropagation}
            {...inputProps}
            {...iconProps}
          />
        )}
        <Typography
          data-testid="day-header"
          className={classes.title}
          variant="subtitle1"
        >
          {headerDay}
        </Typography>
      </div>
      <div className={classes.actionContainer}>
        <Typography
          variant="body2"
          className={classes.duration}
          data-testid={`note-list-item-header-duration-${title}`}
        >
          {totalDuration}
        </Typography>
        {expanded ? collapseIcon : expandIcon}
      </div>
    </ListSubheader>
  );
};

export default SmartDiaryListItemHeader;
